
import React, { useEffect } from "react";
import { LoginForm } from "@/components/auth/LoginForm";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { errorHandler } from "@/lib/error-handler";

const LoginPage: React.FC = () => {
  const location = useLocation();
  const { user, loading, initialized } = useAuth();
  const { toast } = useToast();
  
  // Log initial render (only when auth state changes significantly)
  useEffect(() => {
    console.log("[LoginPage] Auth state changed", {
      hasUser: !!user,
      loading,
      initialized,
      locationState: location.state,
      timestamp: new Date().toISOString()
    });
  }, [!!user, loading, initialized, location.state]);
  
  // Get return path from location state, default to '/meeting'
  const from = location.state?.from || '/meeting';

  // Check for message parameter in URL and show toast
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const message = searchParams.get('message');
    
    if (message) {
      // Use global error handler to prevent duplicate toasts
      if (errorHandler.shouldShowError(message)) {
        toast({
          title: "Account Already Exists",
          description: message,
          variant: "destructive",
        });
      }
      
      // Clean up the URL by removing the message parameter
      const newUrl = new URL(window.location.pathname, window.location.origin);
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search]); // Removed toast from dependencies to prevent infinite loop

  // Handle prospect claiming - auto-start Google OAuth
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const claimToken = searchParams.get('claim_token');
    const prospectSlug = searchParams.get('prospect_slug');
    const action = searchParams.get('action');
    
    if (claimToken && action === 'claim_prospect' && !user) {
      console.log('[LoginPage] Prospect claim detected - auto-starting Google OAuth:', {
        prospectSlug,
        timestamp: new Date().toISOString()
      });
      
      // Save claim token to sessionStorage so it persists through OAuth
      sessionStorage.setItem('claim_token', claimToken);
      sessionStorage.setItem('prospect_slug', prospectSlug || '');
      sessionStorage.setItem('oauth_flow', 'prospect_claim');
      
      // Auto-start Google OAuth using Supabase's built-in OAuth
      import('@/integrations/supabase/client').then(({ supabase }) => {
        supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/onboarding`,
            scopes: 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events',
            queryParams: {
              prompt: 'select_account' // Force Google to show account picker
            }
          }
        }).catch((error) => {
          console.error('[LoginPage] OAuth error:', error);
          toast({
            title: "Authentication Error",
            description: "Failed to start Google sign-in. Please try again.",
            variant: "destructive",
          });
        });
      });
    }
  }, [location.search, user, toast]);

  // Log redirect decision
  useEffect(() => {
    if (user && initialized && !loading) {
      console.log("[LoginPage] User authenticated, should redirect to", {
        from,
        userId: user.id,
        timestamp: new Date().toISOString()
      });
    }
  }, [user, loading, initialized, from]);

  // If authenticated, redirect to the intended page ONLY if auth is fully initialized
  if (user && initialized && !loading) {
    console.log("[LoginPage] Redirecting to", {
      from,
      timestamp: new Date().toISOString()
    });
    return <Navigate to={from} replace />;
  }

  // Show loading if auth state is being determined
  if (loading || !initialized) {
    console.log("[LoginPage] Still loading auth state", {
      loading,
      initialized,
      timestamp: new Date().toISOString()
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Removed excessive logging that was causing console spam

  // Show login form if not authenticated
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link to="/" className="flex items-center gap-2 font-medium">
            <img 
              src="/assets/icons/branding/chronocal-logo.svg" 
              alt="ChronoCal Logo" 
              className="h-6 w-auto" 
            />
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Welcome back</h1>
                <p className="text-balance text-md text-muted-foreground">
                  Enter your details to access your ChronoCal account
                </p>
              </div>
              <LoginForm redirectPath={from} />
            </div>
          </div>
        </div>
      </div>
      <div className="relative hidden bg-muted lg:block">
        <img
          src="/assets/images/ui/4dcfe676-520b-42e3-99c5-f61446691dc1.png"
          alt="ChronoCal DNA Calendar"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

// Ensure this is the default export with explicit typing
export default LoginPage;
